// app/src/lib/sync.ts — connection status + outbox draining (Plan §8.2).
import { useEffect, useState } from "react";
import type { SyncResponse } from "../../../shared/types";
import { api, OfflineError } from "./api";
import { getPending, updateOutbox, clearSynced, countPending } from "../db/idb";

export function useOnline(): boolean {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);
  return online;
}

export interface SyncOutcome { accepted: number; duplicate: number; conflict: number; }

/** Push all pending offline sales for a register. Returns a tally. */
export async function drainOutbox(registerId: string, deviceId?: string): Promise<SyncOutcome> {
  const pending = await getPending();
  if (!pending.length) return { accepted: 0, duplicate: 0, conflict: 0 };

  let resp: SyncResponse;
  try {
    resp = await api.post<SyncResponse>(`/registers/${registerId}/sync`, {
      device_id: deviceId,
      items: pending.map((p) => p.payload),
    });
  } catch (e) {
    if (e instanceof OfflineError) return { accepted: 0, duplicate: 0, conflict: 0 };
    throw e;
  }

  const tally: SyncOutcome = { accepted: 0, duplicate: 0, conflict: 0 };
  for (const r of resp.results) {
    const item = pending.find((p) => p.device_sale_ref === r.device_sale_ref);
    if (!item) continue;
    if (r.status === "conflict") {
      tally.conflict++;
      await updateOutbox({ ...item, status: "conflict", conflict_kind: r.conflict_kind });
    } else {
      r.status === "accepted" ? tally.accepted++ : tally.duplicate++;
      await updateOutbox({ ...item, status: "synced", receipt_no: r.receipt_no });
    }
  }
  await clearSynced();
  return tally;
}

export async function pendingCount(): Promise<number> { return countPending(); }
